import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, ListGroup } from 'react-bootstrap';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { AppContext } from '../Context';
import NavbarGlobal from './NavbarGlobal';
import ReaderTool from '../components/ReaderTool/ReaderTool';
import './DetalleMaterialTeorico.scss';
import '../theme/modal-consistency.scss';
import '../theme/letter-format.scss';
import '../theme/tiptap-editor.scss';

const DetalleMaterialTeorico = () => {
  const { nivel } = useParams();
  const { teacher, currentUser } = React.useContext(AppContext);
  const [lecciones, setLecciones] = useState([]);
  const [leccionSeleccionada, setLeccionSeleccionada] = useState(null);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    let leccionesQuery;
    
    // Si es profesor o master, puede ver todo el nivel seleccionado (o toda la colección si fuera necesario, 
    // pero aquí respetamos el nivel seleccionado en el parámetro para la vista)
    if (teacher || currentUser?.isMaster) {
        leccionesQuery = query(collection(db, 'Lecciones'), where('level', '==', nivel.toString()));
    } else {
        // Para alumnos, solo traemos lo que es menor o igual a su nivel actual 
        // y que coincida con el nivel que están intentando ver (o simplemente <= nivel)
        // Según la regla: "si es nivel 2 ve el nivel 1 y 2".
        // Aquí 'nivel' es el parámetro de la URL (el botón que pulsó).
        // Si el alumno es nivel 2 y pulsó el botón "Ver Nivel 2", el query debe permitir traer 1 y 2.
        leccionesQuery = query(collection(db, 'Lecciones'), where('level', '<=', nivel.toString()));
    }

    const unsubscribe = onSnapshot(leccionesQuery, (snapshot) => {
      const leccionesActualizadas = snapshot.docs
        .map((docSnapshot) => ({
          id: docSnapshot.id,
          ...docSnapshot.data(),
          level: docSnapshot.data().level?.toString(),
        }))
        // Mantenemos el filtro local por si acaso, aunque el query ya debería filtrar
        .filter((leccion) => {
          const normalizedLeccionLevel = leccion.level?.replace('Nivel ', '') || '';
          const leccionNivel = Number.parseInt(normalizedLeccionLevel, 10);
          const nivelActual = Number.parseInt(nivel, 10);
          return leccionNivel <= nivelActual;
        })
        .sort((a, b) => (a.orden || 0) - (b.orden || 0));

      setLecciones(leccionesActualizadas);
      setLeccionSeleccionada(null);
    });

    return () => unsubscribe();
  }, [nivel, teacher, currentUser]);

  const selectedLessonIndex = lecciones.findIndex((leccion) => leccion.id === leccionSeleccionada?.id);
  const documentColumnClass = leccionSeleccionada?.columnasDescripcion === 2 || leccionSeleccionada?.columnasDescripcion === '2'
    ? 'lesson-document--two-columns'
    : 'lesson-document--one-column';

  const leccionesFiltradas = lecciones.filter((leccion) =>
    leccion.titulo?.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleSelectLesson = (leccion) => {
    window.scrollTo(0, 0);
    setLeccionSeleccionada(leccion);
  };

  const handlePreviousLesson = () => {
    window.scrollTo(0, 0);
    if (selectedLessonIndex > 0) {
      setLeccionSeleccionada(lecciones[selectedLessonIndex - 1]);
    }
  };

  const handleNextLesson = () => {
    window.scrollTo(0, 0);
    if (selectedLessonIndex < lecciones.length - 1) {
      setLeccionSeleccionada(lecciones[selectedLessonIndex + 1]);
    }
  };

  return (
    <div className="detalle-container">
      <NavbarGlobal
        title={`Lecciones para el nivel ${nivel}`}
        showBackButton={false}
      />

      <div className="detalle-content lecturas-content">
        {!leccionSeleccionada ? (
          <div className="lecturas-list-view">
            <div className="lecturas-search-container">
              <input
                type="text"
                className="detalle-search-bar lecturas-search"
                placeholder="Buscar leccion..."
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
            </div>

            <div className="lecturas-list-container">
              <h6 className="lecturas-list-title">Lecciones disponibles</h6>
              <ListGroup className="lecturas-list">
                {leccionesFiltradas.map((leccion) => (
                  <button
                    type="button"
                    key={leccion.id}
                    className="lectura-item"
                    onClick={() => handleSelectLesson(leccion)}
                  >
                    <div className="lectura-item-content">
                      <h6 className="lectura-item-title">{leccion.titulo}</h6>
                      <p className="lectura-item-description">
                        {leccion.descripcion
                          ? `${leccion.descripcion.replace(/<[^>]*>/g, '').substring(0, 120)}...`
                          : 'Sin descripcion disponible'}
                      </p>
                      <div className="lectura-item-meta">
                        <span className="lectura-level">Nivel {leccion.level}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </ListGroup>
            </div>
          </div>
        ) : (
          <section className="lectura-detail-section">
            <Button
              variant="outline-secondary"
              className="back-to-list-btn"
              onClick={() => {
                window.scrollTo(0, 0);
                setLeccionSeleccionada(null);
              }}
            >
              Volver a la lista
            </Button>

            <article className="lectura-content lesson-reader">
              {leccionSeleccionada.imagenDestacada && (
                <div className="featured-image-container">
                  <img
                    src={leccionSeleccionada.imagenDestacada}
                    alt="Imagen destacada"
                    className="featured-image"
                  />
                </div>
              )}

              <header className="content-header">
                <span className="content-level">Nivel {leccionSeleccionada.level}</span>
                <h1 className="content-title">{leccionSeleccionada.titulo}</h1>
              </header>

              <ReaderTool
                columnClass={documentColumnClass}
                html={leccionSeleccionada.descripcion || ''}
                storageKey="material-teorico"
              />

              <footer className="content-actions">
                <Button
                  onClick={handlePreviousLesson}
                  disabled={selectedLessonIndex === 0}
                  variant="outline-primary"
                >
                  Leccion anterior
                </Button>
                <Button variant="success">Marcar como completado</Button>
                <Button
                  onClick={handleNextLesson}
                  disabled={selectedLessonIndex === lecciones.length - 1}
                  variant="outline-primary"
                >
                  Siguiente leccion
                </Button>
              </footer>
            </article>
          </section>
        )}
      </div>
    </div>
  );
};

export { DetalleMaterialTeorico };
